import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { AlertErrorIcon } from '@/components/icon'
import { SimpleLogoPageContainer } from '@/components/shared/SimpleLogoPageContainer'
import {
  Badge,
  Box,
  Button,
  Center,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
  Grid,
  Heading,
  HStack,
  PasswordFormatHelperText,
  PasswordInput,
  Stack,
  Text,
} from '@/components/ui'
import { ERROR_MESSAGES } from '@/constants/errorMessages'
import { PASSWORD_FORMAT_REGEX } from '@/constants/passwordFormatRegex'
import { ApiError, useMutation } from '@/lib/api'
import { confirmNewPassword } from '@/lib/auth'

type DialogProps = {
  isOpen: boolean
}

const TimeoutDialog = ({ isOpen }: DialogProps) => {
  const navigate = useNavigate()
  return (
    <DialogRoot open={isOpen} closeOnInteractOutside={false}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>セッションがタイムアウトしました</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Text>再度ログインしてください。</Text>
        </DialogBody>
        <DialogFooter>
          <Button onClick={() => navigate({ to: '/login' })}>
            ログイン画面に戻る
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

const newPasswordFormSchema = z
  .object({
    password: z
      .string()
      .min(1, ERROR_MESSAGES.passwordRequired)
      .min(8, ERROR_MESSAGES.passwordFormat)
      .regex(PASSWORD_FORMAT_REGEX, ERROR_MESSAGES.passwordFormat),
    confirmPassword: z
      .string()
      .min(1, ERROR_MESSAGES.passwordRequired)
      .min(8, ERROR_MESSAGES.passwordFormat)
      .regex(PASSWORD_FORMAT_REGEX, ERROR_MESSAGES.passwordFormat),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: ERROR_MESSAGES.passwordMismatch,
    path: ['confirmPassword'],
  })

type FormValue = z.infer<typeof newPasswordFormSchema>

export const RegisterPasswordPage = () => {
  const [isSessionTimeout, setIsSessionTimeout] = useState(false)

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        setIsSessionTimeout(true)
      },
      3 * 60 * 1000,
    )

    return () => {
      clearTimeout(timeoutId)
    }
  }, [])

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<FormValue>({
    mode: 'onBlur',
    resolver: zodResolver(newPasswordFormSchema),
  })

  const navigate = useNavigate()

  const [apiError, setApiError] = useState<ApiError>()

  const passwordMismatchError =
    errors.confirmPassword?.message === ERROR_MESSAGES.passwordMismatch
      ? errors.confirmPassword
      : undefined

  const { request: submitNewPassword, isPending } = useMutation(
    confirmNewPassword,
    {
      onSuccess: () => {
        navigate({
          to: '/trials',
        })
      },
      onError: error => {
        if (error.message === ERROR_MESSAGES.sessionExpired) {
          setIsSessionTimeout(true)
        } else {
          setApiError(error)
        }
      },
    },
  )

  return (
    <>
      <TimeoutDialog isOpen={isSessionTimeout} />
      <SimpleLogoPageContainer>
        <Stack
          overflow="auto"
          minW="375px"
          flex="1"
          px={{ base: '6', desktop: '8' }}
          gap="12"
          flexDirection="column"
          boxSize="full"
          align="center"
        >
          <Stack align="center" gap="2">
            <Heading fontSize="xl" as="h1" fontWeight="semibold">
              本パスワードの登録
            </Heading>
            <Text>仮パスワードを本パスワードに変更します。</Text>
          </Stack>
          <Box w="full" maxW="2xl">
            <form
              onSubmit={handleSubmit(async data => {
                await submitNewPassword({
                  newPassword: data.password,
                })
              })}
            >
              <Stack gap="12">
                <Grid
                  rowGap="7"
                  columnGap="6"
                  templateColumns={{ base: '1fr', desktop: 'max-content 1fr' }}
                >
                  <FieldRoot
                    display="grid"
                    alignItems="start"
                    gridTemplateColumns="subgrid"
                    gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                    gap={{ base: '1', desktop: '6' }}
                    invalid={!!errors.password}
                  >
                    <HStack
                      w="full"
                      justify={{ base: 'flex-start', desktop: 'space-between' }}
                      gap={{ base: '1', desktop: '4' }}
                    >
                      <FieldLabel
                        fontSize="md"
                        fontWeight="normal"
                        color="text.sub"
                        h="10"
                      >
                        本パスワード
                      </FieldLabel>
                      <Badge variant="subtle" colorPalette="red">
                        必須
                      </Badge>
                    </HStack>
                    <Stack gap="0.5">
                      <PasswordInput
                        {...register('password')}
                        autoComplete="new-password"
                        placeholder="パスワードを入力してください"
                      />
                      {!errors.password && <PasswordFormatHelperText />}
                      <FieldErrorText>
                        {errors.password?.message}
                      </FieldErrorText>
                    </Stack>
                  </FieldRoot>
                  <FieldRoot
                    display="grid"
                    alignItems="start"
                    gridTemplateColumns="subgrid"
                    gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                    gap={{ base: '1', desktop: '6' }}
                    invalid={!!errors.confirmPassword && !passwordMismatchError}
                  >
                    <HStack
                      w="full"
                      justify={{ base: 'flex-start', desktop: 'space-between' }}
                      gap={{ base: '1', desktop: '4' }}
                    >
                      <FieldLabel
                        fontSize="md"
                        fontWeight="normal"
                        color="text.sub"
                        h="10"
                      >
                        本パスワード（確認）
                      </FieldLabel>
                      <Badge variant="subtle" colorPalette="red">
                        必須
                      </Badge>
                    </HStack>
                    <Stack gap="0.5">
                      <PasswordInput
                        {...register('confirmPassword')}
                        placeholder="パスワードを入力してください"
                      />
                      {/* 不一致エラー場合はHelperTextを表示したまま */}
                      {(!errors.confirmPassword || !!passwordMismatchError) && (
                        <PasswordFormatHelperText />
                      )}
                      <FieldErrorText>
                        {errors.confirmPassword?.message}
                      </FieldErrorText>
                    </Stack>
                  </FieldRoot>
                  {(!!apiError || !!passwordMismatchError) && (
                    <HStack
                      gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                      gap="3"
                      justify="center"
                      color="text.error"
                    >
                      <AlertErrorIcon />
                      <Text fontWeight="normal">
                        {apiError
                          ? apiError.message
                          : passwordMismatchError?.message}
                      </Text>
                    </HStack>
                  )}
                </Grid>
                <Center>
                  <Button
                    colorPalette="blue"
                    size="lg"
                    type="submit"
                    disabled={!isValid}
                    loading={isPending}
                  >
                    本パスワードを登録
                  </Button>
                </Center>
              </Stack>
            </form>
          </Box>
        </Stack>
      </SimpleLogoPageContainer>
    </>
  )
}
